import * as React from 'react'

import type { Form } from 'factorial-form'
import { ElectronicSickLeaveEmployeeFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import RadioGroup from 'modules/core/components/Inputs/RadioGroup'
import TextArea from 'modules/core/components/Inputs/TextArea'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import CommonComponents from 'modules/timeoff/components/LeaveContent/CommonComponents'
import { LabourUnitsResult } from 'modules/timeoff/hooks/useLabourUnits'
import type DocumentForm from 'modules/timeoff/lib/DocumentForm'
import type LeaveDayTypeForm from 'modules/timeoff/lib/LeaveDayTypeForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { DaysTree } from 'modules/timeoff/lib/getDateRangeFromTree'
import type { LeaveTypes } from 'modules/timeoff/store/collections/leaveTypes'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import type Leave from 'modules/timeoff/store/models/Leave'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

import { leaveConfirmationFields } from '../../EditLeave/useLeaveData'
import DayTypeForm from './DayTypeForm'
import Documents from './Documents'
import EauResponse from './electronicSickLeave/EauResponse'
import NewEauRequest from './electronicSickLeave/NewEauRequest'

interface Props {
  readOnly?: boolean
  leaveTypeReadOnly: boolean
  form: LeaveForm
  documentForm: DocumentForm
  customFieldsForms: Form[]
  dayTypeForm: LeaveDayTypeForm
  dayTypeOptions: () => any[]
  leave?: Leave
  leaveTypes: LeaveTypes
  onDocumentDropped?: () => void
  bulkAdd?: boolean
  employee?: Employee | null | undefined
  showEmployeeSelector: boolean
  onSelectEmployee?: (Ids: number[] | (number | null | undefined)) => void
  selectedEmployeeIds?: number[]
  isMedicalLeave: boolean
  selectableEmployees: Employee[]
  hasMedicalLeavesOpen: (employeeId: number) => boolean
  allowancesForPolicy: () => Allowance[]
  companyHolidaysMuncher: () => DaysTree
  blockedPeriodsMuncher: () => DaysTree
  leavesMuncher: () => DaysTree
  selectedLeaveType?: LeaveType
  electronicSickLeaveEmployee: ElectronicSickLeaveEmployeeFragment | null | undefined
  leaveConfirmationDetails?: leaveConfirmationFields
  contractVersions: ContractVersions
  timeTaken?: string
  labourUnits: LabourUnitsResult
  isReviewOrEditMode?: boolean
}

const OtherLeavesForm = ({
  readOnly = false,
  leaveTypeReadOnly,
  form,
  documentForm,
  customFieldsForms,
  dayTypeForm,
  dayTypeOptions,
  leave,
  leaveTypes,
  onDocumentDropped,
  bulkAdd = false,
  employee,
  showEmployeeSelector,
  onSelectEmployee,
  selectedEmployeeIds,
  isMedicalLeave,
  selectableEmployees,
  hasMedicalLeavesOpen,
  allowancesForPolicy,
  companyHolidaysMuncher,
  blockedPeriodsMuncher,
  leavesMuncher,
  selectedLeaveType,
  electronicSickLeaveEmployee,
  leaveConfirmationDetails,
  contractVersions,
  timeTaken,
  labourUnits,
  isReviewOrEditMode,
}: Props) => {
  const isMandatory = selectedLeaveType?.attributes.details_required

  const electronicSickLeave = !!electronicSickLeaveEmployee?._policies?.electronicSickLeave.can

  const getErrorMessages = (messages: unknown) => {
    if (!messages || !Array.isArray(messages)) return null

    return messages.map((msg) => {
      if (typeof msg === 'object' && msg !== null && 'error' in msg) {
        return msg
      }
      return { error: String(msg) }
    })
  }

  const isSickLeave = (): boolean => {
    if (leave && leave.leaveType) {
      return !!leave?.leaveType?.isSick
    }

    const leaveTypeId = form.data().leave_type_id

    if (!leaveTypeId) return false
    if (!leaveTypes.length) return false

    const leaveType = leaveTypes.get(leaveTypeId)
    if (!leaveType) return false

    return leaveType.isSick
  }

  return (
    <>
      <CommonComponents
        bulkAdd={bulkAdd}
        leaveTypes={leaveTypes}
        form={form}
        readOnly={readOnly}
        leaveTypeReadOnly={leaveTypeReadOnly}
        leave={leave}
        employee={employee}
        showEmployeeSelector={showEmployeeSelector}
        onSelectEmployee={onSelectEmployee}
        selectedEmployeeIds={selectedEmployeeIds}
        isMedicalLeave={isMedicalLeave}
        selectableEmployees={selectableEmployees}
        hasMedicalLeavesOpen={hasMedicalLeavesOpen}
        allowancesForPolicy={allowancesForPolicy}
        selectedLeaveType={selectedLeaveType}
        contractVersions={contractVersions}
      />

      {hasFeature('timeoff_electronic_sick_leave') &&
        isSickLeave() &&
        electronicSickLeave &&
        (!leave ? (
          <FormRow>
            <NewEauRequest form={form} />
          </FormRow>
        ) : (
          leaveConfirmationDetails &&
          Object.keys(leaveConfirmationDetails).length > 0 && (
            <FormRow>
              <EauResponse
                confirmedStartDate={
                  leaveConfirmationDetails?.confirmed_start_date
                    ? new Date(leaveConfirmationDetails.confirmed_start_date)
                    : null
                }
                confirmedEndDate={
                  leaveConfirmationDetails?.confirmed_end_date
                    ? new Date(leaveConfirmationDetails.confirmed_end_date)
                    : null
                }
                status={leaveConfirmationDetails?.status}
                leave={leave}
                updatedAt={new Date(leaveConfirmationDetails?.updated_at)}
                errorMessages={getErrorMessages(leaveConfirmationDetails?.error_messages)}
              />
            </FormRow>
          )
        ))}

      <FormRow>
        <TextArea
          maxLength={250}
          readonly={readOnly}
          label={i18n.t('time_off.description')}
          field={form.get('description')}
          mandatory={isMandatory}
        />
      </FormRow>
      <FormRow>
        <RadioGroup
          invisibleRadio
          name="dayType"
          theme="toggle"
          readonly={readOnly}
          field={dayTypeForm.get('day_type')}
          options={dayTypeOptions()}
        />
      </FormRow>
      <DayTypeForm
        readOnly={readOnly}
        form={form}
        dayTypeForm={dayTypeForm}
        companyHolidaysMuncher={companyHolidaysMuncher}
        /* Don't pass blocked periods when submitting through omnibutton form */
        blockedPeriodsMuncher={showEmployeeSelector ? () => ({}) : blockedPeriodsMuncher}
        leavesMuncher={leavesMuncher}
        bulkAdd={bulkAdd}
        timeTaken={timeTaken}
        labourUnits={labourUnits}
        isReviewOrEditMode={isReviewOrEditMode}
      />
      {!bulkAdd && form.acceptsAttachment() && (
        <Documents
          customFieldsForms={customFieldsForms}
          documentForm={documentForm}
          employee={employee}
          leave={leave}
          leaveType={leaveTypes.get(form.data().leave_type_id)}
          onDocumentDropped={onDocumentDropped}
          contractVersions={contractVersions}
        />
      )}
    </>
  )
}

export default observer(OtherLeavesForm)
